import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetProjectsQuery,
  GetProjectsData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetProjectsState
  extends MergeRequestState<GetProjectsData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getProjects,
  isFetching: (state: ReduxState) => state.server.getProjects.isFetching,
  data: (state: ReduxState) => state.server.getProjects.data,
  error: (state: ReduxState) => state.server.getProjects.error,
};

const initialState: GetProjectsState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getProjects';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    { query, config }: { query: GetProjectsQuery; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.getProjects(query, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetProjects {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getProjects: ServerGetProjects = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
